// ============================================================================
// File: webapp/src/UploadPopup.js
// Adds a new "Podcast" tab that uses /podcast/resolve + /podcast/ingest.
// Call props.onDone(recordId) to return control to parent (Overview).
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from "react";
import useRecordingAudio from './useRecordingAudio';
import RecordingSources from './RecordingSources';
import { trackRecordingDiagnostics } from './recordingDiagnostics';
import { FiX, FiCheck, FiGlobe, FiChevronDown, FiChevronRight, FiMic, FiMonitor, FiPause, FiPlay } from "react-icons/fi";
import { podcastResolve, podcastIngest, startTranscription } from "./lib/api";
import { useI18n } from "./i18n";
import UpgradeBillingActions from "./UpgradeBillingActions";
import { classifyError, sizeBucket, sizeToMb, trackEvent } from "./analytics";
import { fileExceedsUploadLimit } from "./uploadLimits";
import UploadPreparation from "./UploadPreparation";
import UploadConnectionStatus from './UploadConnectionStatus';
import { importFailure, isSavedUpgrade } from './importRecovery';
import { importMeasurements } from './sourceImport';
import { recordingProblem } from './mediaRecovery.mjs';

const LANGUAGES = [
  { code: "auto", name: "Auto-detect", flag: "🌐" },
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "de", name: "German", flag: "🇩🇪" },
  { code: "fr", name: "French", flag: "🇫🇷" },
  { code: "es", name: "Spanish", flag: "🇪🇸" },
  { code: "it", name: "Italian", flag: "🇮🇹" },
  { code: "pt", name: "Portuguese", flag: "🇵🇹" },
  { code: "nl", name: "Dutch", flag: "🇳🇱" },
  { code: "sv", name: "Swedish", flag: "🇸🇪" },
  { code: "no", name: "Norwegian", flag: "🇳🇴" },
  { code: "da", name: "Danish", flag: "🇩🇰" },
  { code: "fi", name: "Finnish", flag: "🇫🇮" },
  { code: "pl", name: "Polish", flag: "🇵🇱" },
  { code: "cs", name: "Czech", flag: "🇨🇿" },
  { code: "sk", name: "Slovak", flag: "🇸🇰" },
  { code: "sl", name: "Slovenian", flag: "🇸🇮" },
  { code: "hu", name: "Hungarian", flag: "🇭🇺" },
  { code: "ro", name: "Romanian", flag: "🇷🇴" },
  { code: "bg", name: "Bulgarian", flag: "🇧🇬" },
  { code: "hr", name: "Croatian", flag: "🇭🇷" },
  { code: "sr", name: "Serbian", flag: "🇷🇸" },
  { code: "bs", name: "Bosnian", flag: "🇧🇦" },
  { code: "ru", name: "Russian", flag: "🇷🇺" },
  { code: "uk", name: "Ukrainian", flag: "🇺🇦" },
  { code: "el", name: "Greek", flag: "🇬🇷" },
  { code: "tr", name: "Turkish", flag: "🇹🇷" },
  { code: "ar", name: "Arabic", flag: "🇸🇦" },
  { code: "he", name: "Hebrew", flag: "🇮🇱" },
  { code: "fa", name: "Persian", flag: "🇮🇷" },
  { code: "ur", name: "Urdu", flag: "🇵🇰" },
  { code: "hi", name: "Hindi", flag: "🇮🇳" },
  { code: "bn", name: "Bengali", flag: "🇧🇩" },
  { code: "ta", name: "Tamil", flag: "🇮🇳" },
  { code: "te", name: "Telugu", flag: "🇮🇳" },
  { code: "kn", name: "Kannada", flag: "🇮🇳" },
  { code: "ml", name: "Malayalam", flag: "🇮🇳" },
  { code: "mr", name: "Marathi", flag: "🇮🇳" },
  { code: "gu", name: "Gujarati", flag: "🇮🇳" },
  { code: "th", name: "Thai", flag: "🇹🇭" },
  { code: "vi", name: "Vietnamese", flag: "🇻🇳" },
  { code: "id", name: "Indonesian", flag: "🇮🇩" },
  { code: "ms", name: "Malay", flag: "🇲🇾" },
  { code: "zh", name: "Chinese", flag: "🇨🇳" },
  { code: "ja", name: "Japanese", flag: "🇯🇵" },
  { code: "ko", name: "Korean", flag: "🇰🇷" },
  { code: "yue", name: "Cantonese", flag: "🇭🇰" },
  { code: "af", name: "Afrikaans", flag: "🇿🇦" },
  { code: "am", name: "Amharic", flag: "🇪🇹" },
  { code: "as", name: "Assamese", flag: "🇮🇳" },
  { code: "az", name: "Azerbaijani", flag: "🇦🇿" },
  { code: "ba", name: "Bashkir", flag: "🇷🇺" },
  { code: "be", name: "Belarusian", flag: "🇧🇾" },
  { code: "bo", name: "Tibetan", flag: "🇨🇳" },
  { code: "br", name: "Breton", flag: "🇫🇷" },
  { code: "ca", name: "Catalan", flag: "🇪🇸" },
  { code: "cy", name: "Welsh", flag: "🇬🇧" },
  { code: "et", name: "Estonian", flag: "🇪🇪" },
  { code: "eu", name: "Basque", flag: "🇪🇸" },
  { code: "fo", name: "Faroese", flag: "🇫🇴" },
  { code: "gl", name: "Galician", flag: "🇪🇸" },
  { code: "ha", name: "Hausa", flag: "🇳🇬" },
  { code: "haw", name: "Hawaiian", flag: "🇺🇸" },
  { code: "ht", name: "Haitian Creole", flag: "🇭🇹" },
  { code: "hy", name: "Armenian", flag: "🇦🇲" },
  { code: "is", name: "Icelandic", flag: "🇮🇸" },
  { code: "jw", name: "Javanese", flag: "🇮🇩" },
  { code: "ka", name: "Georgian", flag: "🇬🇪" },
  { code: "kk", name: "Kazakh", flag: "🇰🇿" },
  { code: "km", name: "Khmer", flag: "🇰🇭" },
  { code: "la", name: "Latin", flag: "🇻🇦" },
  { code: "lb", name: "Luxembourgish", flag: "🇱🇺" },
  { code: "ln", name: "Lingala", flag: "🇨🇩" },
  { code: "lo", name: "Lao", flag: "🇱🇦" },
  { code: "lt", name: "Lithuanian", flag: "🇱🇹" },
  { code: "lv", name: "Latvian", flag: "🇱🇻" },
  { code: "mg", name: "Malagasy", flag: "🇲🇬" },
  { code: "mi", name: "Maori", flag: "🇳🇿" },
  { code: "mk", name: "Macedonian", flag: "🇲🇰" },
  { code: "mn", name: "Mongolian", flag: "🇲🇳" },
  { code: "mt", name: "Maltese", flag: "🇲🇹" },
  { code: "my", name: "Burmese (Myanmar)", flag: "🇲🇲" },
  { code: "ne", name: "Nepali", flag: "🇳🇵" },
  { code: "nn", name: "Norwegian Nynorsk", flag: "🇳🇴" },
  { code: "oc", name: "Occitan", flag: "🇫🇷" },
  { code: "pa", name: "Punjabi", flag: "🇮🇳" },
  { code: "ps", name: "Pashto", flag: "🇦🇫" },
  { code: "sa", name: "Sanskrit", flag: "🇮🇳" },
  { code: "sd", name: "Sindhi", flag: "🇵🇰" },
  { code: "si", name: "Sinhala", flag: "🇱🇰" },
  { code: "sn", name: "Shona", flag: "🇿🇼" },
  { code: "so", name: "Somali", flag: "🇸🇴" },
  { code: "sq", name: "Albanian", flag: "🇦🇱" },
  { code: "su", name: "Sundanese", flag: "🇮🇩" },
  { code: "sw", name: "Swahili", flag: "🇹🇿" },
  { code: "tg", name: "Tajik", flag: "🇹🇯" },
  { code: "tk", name: "Turkmen", flag: "🇹🇲" },
  { code: "tl", name: "Tagalog", flag: "🇵🇭" },
  { code: "tt", name: "Tatar", flag: "🇷🇺" },
  { code: "uz", name: "Uzbek", flag: "🇺🇿" },
  { code: "yi", name: "Yiddish", flag: "🇮🇱" },
  { code: "yo", name: "Yoruba", flag: "🇳🇬" }
];

// NOTE: keep this file plain JS; no TypeScript types here.

export default function UploadPopup({
  isVisible,
  focused = false,
  onClose,
  onSubmit,
  file,
  files = null,
  uploadItems = [],
  isUploading = false,
  uploadProgress = 0,
  uploadPreparation = null,
  uploadConnection = null,
  jobId = null,
  mode = 'upload',
  isRecording = false,
  onRecordingStateChange,
  isPremium = false,
  signupFirst = false,
  onUpgrade = () => {},
  autoStartRecording = false,
  recordingInput = 'mic',
  onUploadInstead,
  onDone,
  onSavedImport
}) {
  const { t } = useI18n();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [podcastRecovery,setPodcastRecovery] = useState(null);
  const podcastErrorRef = useRef(null);
  const savedPodcastRef = useRef(null);
  useEffect(() => {
    if (!err) return;
    podcastErrorRef.current?.scrollIntoView?.({block:'nearest',behavior:'smooth'});
    podcastErrorRef.current?.focus({preventScroll:true});
  }, [err]);

  // Existing upload popup state
  const [autoStart, setAutoStart] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState("auto");
  const [languageSearch, setLanguageSearch] = useState("");
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [speakerDiarization, setSpeakerDiarization] = useState(true);
  const [speakerCount, setSpeakerCount] = useState("auto");
  const [generateTimestamps, setGenerateTimestamps] = useState(true);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const timerRef = useRef(null);
  const handleSubmitRef = useRef(null);

  // Recording state
  const [recordingTime, setRecordingTime] = useState(0);
  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const recordingErrorRef = useRef(null);
  useEffect(() => {
    if (!isVisible) setRecordingError(null);
  }, [isVisible]);
  useEffect(() => { if (recordingError) recordingErrorRef.current?.focus(); }, [recordingError]);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const recordingTimerRef = useRef(null);

  const mediaRecorderRef = useRef(null);
  const autoStartedRef = useRef(false);
  const recordingStartRef = useRef(0);
  const startingRecordingRef = useRef(false);
  const autoStartCountdownStartedRef = useRef(false);
  const popupVisibleRef = useRef(false);
  const audio = useRecordingAudio({ input: recordingInput, visible: isVisible && mode === 'recording', recording: isRecording });
  const prepareAudio = audio.prepare, closeAudio = audio.close, stopAudioTest = audio.stopTest;
  const recordingDiagnostics = audio.diagnostics;

  // --- Podcast tab state
  const [podcastUrl, setPodcastUrl] = React.useState("");
  const [resolveData, setResolveData] = React.useState(null);
  const [selectedEpisode, setSelectedEpisode] = React.useState(null);
  const [preferTranscript, setPreferTranscript] = React.useState(true);
  const [diarize, setDiarize] = React.useState(true);
  const [diarMethod, setDiarMethod] = React.useState("fast"); // "fast" | "pyannote"
  const [speakerMode, setSpeakerMode] = React.useState("auto"); // "auto" | "exact"
  const [numSpeakers, setNumSpeakers] = React.useState("");
  const [language, setLanguage] = React.useState("auto");
  const [filter, setFilter] = React.useState("");

  React.useEffect(() => {
    if (!isVisible) {
      setPodcastRecovery(null);
      savedPodcastRef.current=null;
      // reset on close
      setBusy(false);
      setErr(null);
      setPodcastUrl("");
      setResolveData(null);
      setSelectedEpisode(null);
      setPreferTranscript(true);
      setDiarize(true);
      setDiarMethod("fast");
      setSpeakerMode("auto");
      setNumSpeakers("");
      setLanguage("auto");
      setFilter("");
    } else {
      // Check if we have pre-resolved podcast data
      if (window.podcastResolveData) {
        const { url, resolveData } = window.podcastResolveData;
        setPodcastUrl(url);
        setResolveData(resolveData);
        // Preselect episode if server provided a best guess
        if (resolveData.episode) setSelectedEpisode(resolveData.episode);
        // Clear the global data
        window.podcastResolveData = null;
      }
    }
  }, [isVisible]);

  async function handleResolve() {
    setErr(null);
    setBusy(true);
    try {
      trackEvent("podcast_resolve_attempt", { source: "upload_popup" });
      const data = await podcastResolve(podcastUrl.trim());
      setResolveData(data);
      trackEvent("podcast_resolve_success", {
        source: "upload_popup",
        has_episode_match: Boolean(data.episode),
        episode_count: Array.isArray(data.episodes) ? data.episodes.length : undefined,
      });
      // Preselect episode if server provided a best guess (Apple ?i=xxx case)
      if (data.episode) setSelectedEpisode(data.episode);
    } catch (e) {
      trackEvent("podcast_resolve_error", {
        source: "upload_popup",
        error_type: classifyError(e),
        error_code: e.code || importFailure(e.payload,e.status,'podcast',e).code,
        status_code:e.status, operation:'podcast_resolve',
      });
      setResolveData(null);
      setSelectedEpisode(null);
      setErr(e?.message || t("upload.resolveFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function handleImportAndTranscribe() {
    if (!selectedEpisode) {
      setErr(t("upload.pickEpisode"));
      return;
    }
    setErr(null);
    setPodcastRecovery(null);
    setBusy(true);
    try {
      trackEvent("podcast_import_attempt", {
        prefer_publisher_transcript: preferTranscript,
        diarize,
        language,
        speaker_count_mode: speakerMode,
      });
      if (savedPodcastRef.current?.episode!==selectedEpisode) savedPodcastRef.current=null;
      const ing = savedPodcastRef.current?.result || await podcastIngest(selectedEpisode, preferTranscript);
      savedPodcastRef.current={episode:selectedEpisode,result:ing};

      // Path A: imported publisher transcript (completed immediately)
      if (ing.status === "completed") {
        trackEvent("podcast_import_completed", {
          source: "publisher_transcript",
          prefer_publisher_transcript: preferTranscript,
        });
        onDone?.(ing.id,{completed:true});
        onClose();
        return;
      }

      // Path B: audio was ingested → start transcription
      const args = { id: ing.id, diarize, diar_method: diarMethod };
      if (language && language !== "auto") args.language = language;

      if (diarize && speakerMode === "exact" && numSpeakers !== "") {
          args.num_speakers = Number(numSpeakers);       // if supported
          args.min_speakers = Number(numSpeakers);       // portable fallback
          args.max_speakers = Number(numSpeakers);
      }

      await startTranscription(args);
      trackEvent("podcast_transcription_started", {
        prefer_publisher_transcript: preferTranscript,
        diarize,
        language: args.language || "auto",
        speaker_count_mode: speakerMode,
      });
      onDone?.(ing.id);
      onClose();
    } catch (e) {
      const saved = isSavedUpgrade(e.payload,e.status);
      const failure = importFailure(e.payload,e.status,'podcast',e);
      trackEvent(saved ? 'podcast_import_requires_upgrade' : 'podcast_import_error', {
        error_type:saved ? 'plan_limit' : failure.kind,
        error_code:saved ? 'free_duration_limit' : failure.code, status_code:e.status,
        operation:e.operation || 'podcast_import', ...importMeasurements(e.payload),
      });
      if (saved && onSavedImport) {
        await onSavedImport(e.payload,e.status);
        return;
      }
      setPodcastRecovery(failure);
      setErr(t(failure.messageKey));
    } finally {
      setBusy(false);
    }
  }

  // ---------- Recording lifecycle ----------
  const fullCleanup = useCallback((keepRecording = false) => {
    recordingStartRef.current += 1;
    startingRecordingRef.current = false;
    setIsStartingRecording(false);
    const recorder = mediaRecorderRef.current;
    if (recorder) {
      recorder.discard = !keepRecording;
      try { if (recorder.state !== 'inactive') recorder.stop(); } catch {}
    }
    closeAudio();
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    recordingTimerRef.current = null;
    onRecordingStateChange?.(false);
    setIsPaused(false);
  }, [closeAudio, onRecordingStateChange]);

  useEffect(() => () => {
    popupVisibleRef.current = false;
    autoStartedRef.current = false;
    fullCleanup();
  }, [fullCleanup]);

  // Reset form when popup opens/closes
  useEffect(() => {
    if (popupVisibleRef.current === isVisible) return;
    popupVisibleRef.current = isVisible;

    if (isVisible) {
      setAutoStart(true);
      setSelectedLanguage("auto");
      setLanguageSearch("");
      setShowLanguageDropdown(false);
      setSpeakerDiarization(true);
      setSpeakerCount("auto");
      setGenerateTimestamps(true);
      setShowAdvancedOptions(false);
      setCountdown(10);
      setIsCountingDown(false);
      autoStartCountdownStartedRef.current = false;

      setRecordingTime(0);
      setIsPaused(false);
      setRecordedBlob(null);

      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      autoStartCountdownStartedRef.current = false;
      if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
      fullCleanup();

    }
  }, [isVisible, fullCleanup]);

  // A source check reuses these streams; starting never repeats the sharing picker.
  const startRecording = useCallback(async () => {
    if (!popupVisibleRef.current || startingRecordingRef.current ||
        (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive')) return;
    const startId = ++recordingStartRef.current;
    setRecordingError(null);
    startingRecordingRef.current = true;
    setIsStartingRecording(true);
    trackEvent('recording_permission_request', { source: recordingInput === 'mic' ? 'recording_mic' : recordingInput === 'tab' ? 'recording_tab' : 'recording_tab_mic' });
    try {
      const session = await prepareAudio();
      if (startId !== recordingStartRef.current || !popupVisibleRef.current) return;
      stopAudioTest();
      session.resetSignals();
      const types = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4;codecs=mp4a.40.2'];
      const mimeType = types.find(type => MediaRecorder.isTypeSupported(type)) || '';
      const recorder = new MediaRecorder(session.outputStream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 24000 });
      mediaRecorderRef.current = recorder;
      const chunks = [];
      recorder.ondataavailable = event => { if (event.data?.size > 0) chunks.push(event.data); };
      recorder.onstop = () => {
        if (recorder.discard || !popupVisibleRef.current) return;
        const blob = new Blob(chunks, { type: mimeType || recorder.mimeType || 'audio/webm' });
        setRecordedBlob(blob);
        if (recorder.autoSubmitAfterStop) {
          recorder.autoSubmitAfterStop = false;
          const options = { language: selectedLanguage === 'auto' ? null : selectedLanguage,
            diarize: speakerDiarization, min_speakers: speakerCount === 'auto' ? null : parseInt(speakerCount),
            max_speakers: speakerCount === 'auto' ? null : parseInt(speakerCount), timestamps: generateTimestamps,
            recording_diagnostics: recordingDiagnostics() };
          Promise.resolve(onSubmit(options, blob)).catch(error => console.error('Failed to submit recording:', error));
        }
      };
      recorder.start();
      trackEvent('recording_started', { source: recordingInput === 'tab-mic' ? 'recording_tab_mic' : recordingInput === 'tab' ? 'recording_tab' : 'recording_mic', mime_type: mimeType || recorder.mimeType || 'unknown' });
      onRecordingStateChange?.(true);
      recordingTimerRef.current = setInterval(() => setRecordingTime(previous => previous + 1), 1000);
    } catch (error) {
      if (startId !== recordingStartRef.current || !popupVisibleRef.current || error.code === 'request_cancelled') return;
      fullCleanup();
      const failure = recordingProblem(error, error.captureStage || (recordingInput === 'mic' ? 'microphone' : 'tab'));
      setRecordingError(failure);
      trackEvent('recording_start_error', { source: recordingInput === 'tab-mic' ? 'recording_tab_mic' : recordingInput === 'tab' ? 'recording_tab' : 'recording_mic', error_type: classifyError(error), error_code: failure.code, operation: 'recording_start' });
    } finally {
      if (startId === recordingStartRef.current) { startingRecordingRef.current = false; setIsStartingRecording(false); }
    }
  }, [prepareAudio, stopAudioTest, fullCleanup, generateTimestamps, onRecordingStateChange, onSubmit, selectedLanguage, speakerCount, speakerDiarization, recordingInput, recordingDiagnostics]);

  const handleAudioTest = async () => {
    setRecordingError(null);
    try { await audio.startTest(); }
    catch (error) {
      if (!popupVisibleRef.current || error.code === 'request_cancelled') return;
      const failure = recordingProblem(error, error.captureStage || 'microphone');
      setRecordingError(failure);
      trackEvent('recording_audio_check_error', { source: recordingInput === 'tab-mic' ? 'recording_tab_mic' : recordingInput === 'tab' ? 'recording_tab' : 'recording_mic', error_code: failure.code, operation: 'recording_audio_check' });
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      audio.setPaused(true);
      trackEvent("recording_pause", {
        source: recordingInput === "tab-mic"
          ? "recording_tab_mic"
          : (recordingInput === "tab" ? "recording_tab" : "recording_mic"),
        duration_seconds: recordingTime,
      });
      setIsPaused(true);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      audio.setPaused(false);
      trackEvent("recording_resume", {
        source: recordingInput === "tab-mic"
          ? "recording_tab_mic"
          : (recordingInput === "tab" ? "recording_tab" : "recording_mic"),
        duration_seconds: recordingTime,
      });
      setIsPaused(false);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      trackEvent("recording_stop", {
        source: recordingInput === "tab-mic"
          ? "recording_tab_mic"
          : (recordingInput === "tab" ? "recording_tab" : "recording_mic"),
        duration_seconds: recordingTime, ...recordingDiagnostics(),
      });
      trackRecordingDiagnostics('recording_stop', recordingDiagnostics());
      mediaRecorderRef.current.stop();
    }
    fullCleanup(true);
  }, [fullCleanup, recordingInput, recordingTime, recordingDiagnostics]);

  // Format recording time display
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const effectiveFiles = Array.isArray(files) && files.length
    ? files
    : (file ? [file] : []);
  const isBatch = effectiveFiles.length > 1;
  const hasUploadItems = Array.isArray(uploadItems) && uploadItems.length > 0;
  const hasJobId = hasUploadItems
    ? uploadItems.some(item => item.jobId && item.status !== "requires_upgrade")
    : !!jobId;
  const isUploaded = hasUploadItems
    ? uploadItems.every(item => item.status === "uploaded" || item.status === "start_error" || item.jobId)
    : Number(uploadProgress) >= 100;
  // Upload completion and server preparation are separate steps.
  const preparation = hasUploadItems ? uploadItems[0]?.preparation : uploadPreparation;
  const connection = hasUploadItems ? uploadItems[0]?.connection : uploadConnection;
  const displayProgress = hasJobId || preparation ? 100 : Math.min(Number(uploadProgress) || 0, 99);
  const isFinalizingUpload = !hasJobId && (preparation || Number(uploadProgress) >= 100);

  const handleSubmit = useCallback(async () => {
    const options = {
      language: selectedLanguage === "auto" ? null : selectedLanguage,
      diarize: speakerDiarization,
      min_speakers: speakerCount === "auto" ? null : parseInt(speakerCount),
      max_speakers: speakerCount === "auto" ? null : parseInt(speakerCount),
      timestamps: generateTimestamps,
      ...(mode === 'recording' ? { recording_diagnostics: recordingDiagnostics() } : {})
    };
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsCountingDown(false);
    setCountdown(10);

    if (mode === 'recording' && recordedBlob) {
      trackEvent("recording_upload_submit", {
        source: recordingInput === "tab-mic"
          ? "recording_tab_mic"
          : (recordingInput === "tab" ? "recording_tab" : "recording_mic"),
        duration_seconds: recordingTime,
        size_mb: sizeToMb(recordedBlob.size),
        size_bucket: sizeBucket(recordedBlob.size),
      });
      await onSubmit(options, recordedBlob);
    } else {
      trackEvent("upload_popup_submit", {
        source: mode === "upload" ? "upload_popup" : "recording_popup",
        language: options.language || "auto",
        diarize: options.diarize,
        timestamps: options.timestamps,
        speaker_count_mode: options.min_speakers || options.max_speakers ? "specified" : "auto",
      });
      await onSubmit(options);
    }
  }, [selectedLanguage, speakerDiarization, speakerCount, generateTimestamps, onSubmit, mode, recordedBlob, recordingInput, recordingTime, recordingDiagnostics]);

  // Keep latest submit handler for countdown
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Auto-start countdown once per opened popup after the first server job id exists.
  useEffect(() => {
    if (!isVisible) return;
    if (!isBatch && isUploading) return;
    if (!hasJobId) return;               // <- require job id to avoid races
    if (!autoStart) return;
    if (timerRef.current) return;
    if (autoStartCountdownStartedRef.current) return;

    autoStartCountdownStartedRef.current = true;
    setIsCountingDown(true);
    setCountdown(10);

    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setIsCountingDown(false);
          if (handleSubmitRef.current) handleSubmitRef.current();
          return 10;
        }
        return prev - 1;
      });
    }, 1000);
  }, [isVisible, isUploading, hasJobId, autoStart, isBatch]);

  useEffect(() => {
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, []);

  // Auto-start recording when the popup opens in "recording" mode
  useEffect(() => {
    if (!isVisible) {
      // reset one-shot guard when the dialog is closed
      autoStartedRef.current = false;
      return;
    }
    if (
      mode === 'recording' &&
      autoStartRecording &&
      !autoStartedRef.current &&
      !isRecording &&
      !recordedBlob
    ) {
      autoStartedRef.current = true;
      // start immediately to keep user-gesture context where possible
      startRecording();
    }
  }, [autoStartRecording, isVisible, isRecording, mode, recordedBlob, startRecording]);

  const filteredLanguages = LANGUAGES.filter(lang =>
    lang.name.toLowerCase().includes(languageSearch.toLowerCase()) ||
    lang.code.toLowerCase().includes(languageSearch.toLowerCase())
  );
  const selectedLangObj = LANGUAGES.find(lang => lang.code === selectedLanguage) || LANGUAGES[0];

  const displayItems = isBatch
    ? (hasUploadItems
      ? uploadItems
      : effectiveFiles.map((f, idx) => ({
        id: `file-${idx}`,
        file: f,
        status: "queued",
        progress: 0,
        error: null
      })))
    : [];

  const statusLabel = (item) => {
    const pct = item.progress != null ? Math.round(item.progress) : null;
    switch (item.status) {
      case "queued":
        return t("upload.queued");
      case "uploading":
        if (item.preparation && !item.jobId) return t("upload.transferComplete");
        return pct != null ? t("upload.uploadingPct", { percent: pct }) : t("upload.uploading");
      case "uploaded":
        return t("upload.uploaded");
      case "requires_upgrade":
        return t("overview.statusRequiresUpgrade");
      case "start_error":
        return t("upload.startFailed");
      case "canceled":
        return t("upload.canceled");
      case "error":
        return t("upload.error");
      default:
        return item.status || "";
    }
  };

  const shouldShowItemError = (item) =>
    Boolean(item.error) && !["uploading", "queued", "uploaded", "canceled"].includes(item.status);

  const handleCancel = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsCountingDown(false);
    setCountdown(10);
    trackEvent("upload_popup_cancel", {
      mode,
      recording_input: mode === "recording" ? recordingInput : undefined,
      upload_in_progress: isUploading,
      has_job_id: hasJobId,
    });
    onClose();
  };

  // Show row while uploading or once we have an id; start only once we have id
  const showAutoStartRow = mode === 'upload' && (isUploading || hasJobId);
  const exceedsSizeLimit = mode === 'upload' && effectiveFiles.some(file => fileExceedsUploadLimit(file, isPremium));
  // Disable Start until we have the server id, to avoid "finalizing" race
  const startDisabled = (mode === 'upload' && !hasJobId) || exceedsSizeLimit;

  if (!isVisible) return null;

  return (
    <div className="upload-popup-overlay">
      <div className="upload-popup">
        <div className="upload-popup-header">
          <h3>
            {mode === 'recording'
              ? (recordingInput === 'tab-mic'
                ? t("upload.recordTabMicAudio")
                : (recordingInput === 'tab' ? t("upload.recordTabAudio") : t("upload.recordTranscribe")))
              : (resolveData ? t("upload.selectPodcast") : t("upload.addAudio"))
            }
          </h3>
          <button
            className="close-button"
            onClick={handleCancel}
            aria-label={t("common.close")}
          >
            <FiX />
          </button>
        </div>

        {/* No tabs - podcast is the default and only option for upload mode */}

        {/* Content */}
        <div className="upload-popup-content">
          {mode === 'upload' && !resolveData && (
            <>
              <div className="file-info-section">
                {isBatch ? (
                  <>
                    <h4>{t("upload.filesSelected", { count: effectiveFiles.length })}</h4>
                    <ul className="file-list">
                      {displayItems.map(item => (
                        <li key={item.id} className="file-row">
                          <div className="file-row-main">
                            <span className="file-name">{item.file?.name || t("upload.untitled")}</span>
                            <span className="file-status">{statusLabel(item)}</span>
                          </div>
                          {item.status === "uploading" && (
                            <div className="file-progress">
                              <div className="file-progress-bar" style={{ width: `${item.jobId || item.preparation ? 100 : Math.min(item.progress || 0, 99)}%` }} />
                            </div>
                          )}
                          {item.status === "uploading" && item.preparation && (
                            <UploadPreparation preparation={item.preparation} compact />
                          )}
                          {item.status === 'uploading' && !item.preparation && <UploadConnectionStatus connection={item.connection} />}
                          {shouldShowItemError(item) && (
                            <div className="file-error">{item.error}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                    <p className="file-batch-note">{t("upload.optionsAll")}</p>
                  </>
                ) : (
                  <>
                    <h4>{t("upload.file")} {file?.name}</h4>
                    <p>{t("upload.size")} {file?.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : t("upload.unknown")}</p>
                    {exceedsSizeLimit && (
                      <p style={{marginTop: 8, color: '#b91c1c', fontWeight: 600}}>
                        {t(isPremium ? "upload.premiumSizeExceeded" : "upload.freeSizeExceeded")}
                      </p>
                    )}
                  </>
                )}
            </div>

              {!isBatch && isUploading && (
                <div className={`upload-progress-section${preparation ? " upload-progress-section--complete" : ""}`}>
                  <div className="progress-container">
                    <div className="progress-bar" style={{ width: `${displayProgress}%` }} />
                  </div>
                  {preparation ? <UploadPreparation preparation={preparation} /> : <p>
                    {isFinalizingUpload
                      ? t("upload.finalizingUpload")
                      : t("upload.uploadingPct", { percent: displayProgress })}
                  </p>}
                  {!preparation && <UploadConnectionStatus connection={connection} />}
                </div>
              )}
            </>
        )}

          {mode === 'upload' && resolveData && (
            <>
              <div className="space-y-4">
                {!resolveData && (
                  <>
                <label className="block">
                  <div className="text-sm font-medium mb-1">{t("upload.podcastLink")}</div>
                  <input
                    className="w-full rounded-xl border px-3 py-2"
                    placeholder={t("upload.podcastPlaceholder")}
                    value={podcastUrl}
                    onChange={(e) => setPodcastUrl(e.target.value)}
                    disabled={busy}
                  />
                </label>

                <div className="flex items-center gap-3">
                  <button
                    className="rounded-xl px-4 py-2 bg-blue-600 text-white disabled:opacity-50"
                    onClick={handleResolve}
                    disabled={busy || !podcastUrl.trim()}
                  >
                    {busy ? t("upload.resolving") : t("upload.resolve")}
                  </button>

                  {err && <div className="text-red-600 text-sm">{err}</div>}
                </div>
                  </>
                )}

                {resolveData && (
                  <div className="mt-2">
                    {err && <div className="podcast-import-error" ref={podcastErrorRef}
                      role="alert" tabIndex={-1}>
                      <strong>{t('import.failedTitle')}</strong><p>{err}</p>
                      {podcastRecovery?.retryable && <button type="button" className="secondary-button"
                        disabled={busy} onClick={handleImportAndTranscribe}>{t('workspace.retry')}</button>}
                      {!isPremium && (podcastRecovery?.kind==='plan_limit' || podcastRecovery?.upgradeAvailable)
                        && <UpgradeBillingActions onUpgrade={onUpgrade} signupFirst={signupFirst} />}
                    </div>}
                    <div className="mb-3">
                      <div className="flex items-center justify-between">
                        <div>
                      <div className="text-sm text-gray-500">{t("upload.feed")}</div>
                      <div className="font-medium">{resolveData.show?.title || "Podcast"}</div>
                      {resolveData.kind === "episode" && (
                        <div className="text-xs text-gray-500">{t("upload.directEpisode")}</div>
                      )}
                        </div>
                        <button
                          className="text-sm text-blue-600 hover:text-blue-800"
                          onClick={() => {
                            setResolveData(null);
                            setSelectedEpisode(null);
                            setPodcastUrl("");
                          }}
                        >
                          {t("upload.change")}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium">{t("upload.episodes")}</div>
                      <input
                        className="rounded-lg border px-2 py-1 text-sm"
                        placeholder={t("upload.filterEpisodes")}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                      />
                    </div>

                    <div className="border rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-3 py-2">{t("upload.title")}</th>
                            <th className="text-left px-3 py-2">{t("upload.date")}</th>
                            <th className="text-left px-3 py-2">{t("upload.transcript")}</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {resolveData.episodes
                            .filter((e) =>
                              filter
                                ? (e.title || "").toLowerCase().includes(filter.toLowerCase())
                                : true
                            )
                            .map((e, idx) => {
                              const selected =
                                selectedEpisode &&
                                (selectedEpisode.guid && e.guid
                                  ? selectedEpisode.guid === e.guid
                                  : selectedEpisode.title === e.title);
                              return (
                                <tr
                                  key={`${e.guid || e.title || idx}`}
                                  className={selected ? "bg-blue-50" : ""}
                                >
                                  <td className="px-3 py-2">{e.title}</td>
                                  <td className="px-3 py-2">
                                    {e.pub_date_iso
                                      ? new Date(e.pub_date_iso).toLocaleDateString()
                                      : "-"}
                                  </td>
                                  <td className="px-3 py-2">
                                    {e.transcript_url ? (
                                      <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs">
                                        {t("upload.hasTranscript")}
                                      </span>
                                    ) : (
                                      <span className="text-xs text-gray-400">{t("upload.none")}</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <button
                                      className={`rounded-lg px-3 py-1 border ${
                                        selected ? "bg-blue-600 text-white" : "hover:bg-gray-50"
                                      }`}
                                      onClick={() => setSelectedEpisode(e)}
                                    >
                                      {selected ? t("upload.selected") : t("upload.select")}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>

                    {/* Options - reuse the same UI as recording mode */}
                    <div className="transcription-options">
                      <div className="option-section">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={preferTranscript}
                            onChange={(e) => setPreferTranscript(e.target.checked)}
                          />
                          {t("upload.preferPublisher")}
                        </label>
                      </div>

                      {/* Language */}
                      <div className="option-section">
                        <label className="option-label">{t("upload.language")}</label>
                        <div className="language-selector">
                          <div className="language-input" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
                            <span className="language-flag">{LANGUAGES.find(lang => lang.code === language)?.flag || "🌐"}</span>
                            <span className="language-name">{LANGUAGES.find(lang => lang.code === language)?.name || "Auto-detect"}</span>
                            <FiGlobe className="language-icon" />
                          </div>

                          {showLanguageDropdown && (
                            <div className="language-dropdown">
                              <input
                                type="text"
                                placeholder={t("upload.searchLanguages")}
                                value={languageSearch}
                                onChange={(e) => setLanguageSearch(e.target.value)}
                                className="language-search"
                                autoFocus
                              />
                              <div className="language-list">
                                {filteredLanguages.map(lang => (
                                  <div
                                    key={lang.code}
                                    className={`language-option ${lang.code === language ? 'selected' : ''}`}
                                    onClick={() => {
                                      setLanguage(lang.code);
                                      setShowLanguageDropdown(false);
                                      setLanguageSearch("");
                                    }}
                                  >
                                    <span className="language-flag">{lang.flag}</span>
                                    <span className="language-name">{lang.name}</span>
                                    {lang.code === language && <FiCheck className="check-icon" />}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Advanced Options */}
                      <div className="advanced-options-section">
                        <div
                          className="advanced-options-header"
                          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                        >
                          {showAdvancedOptions ? <FiChevronDown /> : <FiChevronRight />}
                          <span>{t("upload.advanced")}</span>
                        </div>

                        {showAdvancedOptions && (
                          <div className="advanced-options-content">
                            <div className="option-section">
                              <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={diarize}
                            onChange={(e) => setDiarize(e.target.checked)}
                          />
                                <span>{t("upload.speakerIdentification")}</span>
                        </label>
                        </div>

                        {diarize && (
                              <div className="option-section">
                                <label className="option-label">{t("upload.numberSpeakers")}</label>
                              <select
                                  value={speakerMode === "auto" ? "auto" : numSpeakers}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "auto") {
                                      setSpeakerMode("auto");
                                    } else {
                                      setSpeakerMode("exact");
                                      setNumSpeakers(value);
                                    }
                                  }}
                                  className="speaker-select"
                                >
                                  <option value="auto">{t("upload.autoDetect")}</option>
                                  <option value="1">{t("upload.oneSpeaker")}</option>
                                  <option value="2">{t("upload.nSpeakers", { count: 2 })}</option>
                                  <option value="3">{t("upload.nSpeakers", { count: 3 })}</option>
                                  <option value="4">{t("upload.nSpeakers", { count: 4 })}</option>
                                  <option value="5">{t("upload.nSpeakers", { count: 5 })}</option>
                                  <option value="6">{t("upload.nSpeakers", { count: 6 })}</option>
                                  <option value="7">{t("upload.nSpeakers", { count: 7 })}</option>
                                  <option value="8">{t("upload.nSpeakers", { count: 8 })}</option>
                              </select>
                            </div>
                            )}

                              </div>
                            )}
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        - {t("upload.publisherTranscriptInfo")}<br />
                        - {t("upload.normalTranscriptionInfo")}
                      </div>
                      <button
                        className="primary-button"
                        disabled={busy || !selectedEpisode}
                        onClick={handleImportAndTranscribe}
                      >
                        {busy ? t("upload.working") : t("upload.importTranscribe")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}


          {mode === 'recording' && (
            <>
              <div className="recording-section">
                {recordingError && <div className="recording-recovery" role="alert" tabIndex={-1} ref={recordingErrorRef}>
                  <strong>{t('upload.recordingProblemTitle')}</strong>
                  <p>{t(`upload.recording.${recordingError.key}`)}</p>
                  {onUploadInstead && <button type="button" className="secondary-button" onClick={() => {
                    trackEvent('recording_recovery_action', { error_code: recordingError.code, operation: 'recording_start', event_source: 'upload_file' });
                    onUploadInstead();
                  }}>{t('overview.uploadOriginal')}</button>}
                </div>}
                <RecordingSources audio={audio} input={recordingInput} isRecording={isRecording} isPaused={isPaused} recorded={Boolean(recordedBlob)} onTest={handleAudioTest} />
                <div className="recording-controls">
                  {!isRecording && !recordedBlob && (
                    <button className="record-button start-record" onClick={startRecording} disabled={isStartingRecording || audio.preparing} aria-busy={isStartingRecording || audio.preparing}>
                      {recordingInput === 'tab-mic' ? (
                        <span className="recording-source-icons" aria-hidden="true">
                          <FiMonitor className="recording-source-screen" />
                          <FiMic className="recording-source-mic" />
                        </span>
                      ) : (
                        recordingInput === 'mic' ? <FiMic /> : <FiMonitor />
                      )}
                      {isStartingRecording ? t("upload.startingRecording") : recordingInput === 'tab-mic'
                        ? t("upload.startTabMicCapture")
                        : (recordingInput === 'tab' ? t("upload.startTabCapture") : t("upload.startRecording"))}
                    </button>
                  )}

                  {isRecording && (
                    <div className="recording-active">
                      <div className="recording-indicator">
                        <span className="recording-dot"></span>
                        {recordingInput === 'tab-mic'
                          ? t("upload.recordingTabMic")
                          : (recordingInput === 'tab' ? t("upload.recordingTab") : t("upload.recording"))}
                      </div>
                      <div className="recording-time">{formatRecordingTime(recordingTime)}</div>

                      <div className="recording-buttons">
                        <button
                          className="record-button pause-resume"
                          onClick={isPaused ? resumeRecording : pauseRecording}
                        >
                          {isPaused ? <FiPlay /> : <FiPause />}
                          {isPaused ? t("upload.resume") : t("upload.pause")}
                        </button>
                        <button
                          className="primary-button transcribe-button"
                          onClick={() => {
                            if (mediaRecorderRef.current) {
                              mediaRecorderRef.current.autoSubmitAfterStop = true;
                            }
                            stopRecording();
                          }}
                        >
                          {t("upload.transcribe")}
                        </button>
                      </div>
                    </div>
                  )}

                  {recordedBlob && !isUploading && (
                    <div className="recording-complete">
                      <div className="recording-info">
                        <h4>{t("upload.recordingComplete")}</h4>
                        <p>{t("upload.duration")}: {formatRecordingTime(recordingTime)}</p>
                      </div>

                      <div className="recording-buttons">
                        <button
                          className="primary-button transcribe-button"
                          onClick={handleSubmit}   // will call onSubmit(options, recordedBlob)
                        >
                          {t("upload.transcribe")}
                        </button>

                        <button
                          className="record-button new-record"
                          onClick={() => {
                            setRecordedBlob(null);
                            setRecordingTime(0);
                          }}
                        >
                          {recordingInput === 'tab-mic'
                            ? t("upload.captureAnotherTabMic")
                            : (recordingInput === 'tab' ? t("upload.captureAnotherTab") : t("upload.recordAgain"))}
                        </button>
                      </div>
                    </div>
                  )}

                  {isUploading && (
                    <div className="upload-progress-section">
                      <div className="progress-container">
                        <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
                      </div>
                      <p>{t("upload.uploadingRecording", { percent: uploadProgress })}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Options - for recording mode and normal file uploads */}
          {(mode === 'recording' || (mode === 'upload' && !resolveData)) && (
            <div className="transcription-options">
              {showAutoStartRow && (
                <div className="option-section">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={autoStart}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setAutoStart(next);
                        if (!next) {
                          if (timerRef.current) {
                            clearInterval(timerRef.current);
                            timerRef.current = null;
                          }
                          setIsCountingDown(false);
                          setCountdown(10);
                        }
                      }}
                    />
                    <span>
                      {hasJobId && isCountingDown && autoStart
                        ? (isBatch
                          ? t("upload.autoStartAllCountdown", { count: countdown })
                          : t("upload.autoStartOneCountdown", { count: countdown }))
                        : (isBatch
                          ? t("upload.autoStartAll")
                          : t("upload.autoStartOne"))}
                    </span>
                  </label>
                </div>
              )}

              {/* Language */}
              <div className="option-section">
                <label className="option-label">{t("upload.language")}</label>
                <div className="language-selector">
                  <div className="language-input" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
                    <span className="language-flag">{selectedLangObj.flag}</span>
                    <span className="language-name">{selectedLangObj.name}</span>
                    <FiGlobe className="language-icon" />
                  </div>

                  {showLanguageDropdown && (
                    <div className="language-dropdown">
                      <input
                        type="text"
                        placeholder={t("upload.searchLanguages")}
                        value={languageSearch}
                        onChange={(e) => setLanguageSearch(e.target.value)}
                        className="language-search"
                        autoFocus
                      />
                      <div className="language-list">
                        {filteredLanguages.map(lang => (
                          <div
                            key={lang.code}
                            className={`language-option ${lang.code === selectedLanguage ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedLanguage(lang.code);
                              setShowLanguageDropdown(false);
                              setLanguageSearch("");
                            }}
                          >
                            <span className="language-flag">{lang.flag}</span>
                            <span className="language-name">{lang.name}</span>
                            {lang.code === selectedLanguage && <FiCheck className="check-icon" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Advanced Options */}
              <div className="advanced-options-section">
                <div
                  className="advanced-options-header"
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                >
                  {showAdvancedOptions ? <FiChevronDown /> : <FiChevronRight />}
                  <span>{t("upload.advanced")}</span>
                </div>

                {showAdvancedOptions && (
                  <div className="advanced-options-content">
                    <div className="option-section">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={speakerDiarization}
                          onChange={(e) => setSpeakerDiarization(e.target.checked)}
                        />
                        <span>{t("upload.speakerIdentification")}</span>
                      </label>
                    </div>

                    {speakerDiarization && (
                      <div className="option-section">
                        <label className="option-label">{t("upload.numberSpeakers")}</label>
                        <select
                          value={speakerCount}
                          onChange={(e) => setSpeakerCount(e.target.value)}
                          className="speaker-select"
                        >
                          <option value="auto">{t("upload.autoDetect")}</option>
                          <option value="1">{t("upload.oneSpeaker")}</option>
                          <option value="2">{t("upload.nSpeakers", { count: 2 })}</option>
                          <option value="3">{t("upload.nSpeakers", { count: 3 })}</option>
                          <option value="4">{t("upload.nSpeakers", { count: 4 })}</option>
                          <option value="5">{t("upload.nSpeakers", { count: 5 })}</option>
                          <option value="6">{t("upload.nSpeakers", { count: 6 })}</option>
                          <option value="7">{t("upload.nSpeakers", { count: 7 })}</option>
                          <option value="8">{t("upload.nSpeakers", { count: 8 })}</option>
                        </select>
                      </div>
                    )}

                    <div className="option-section">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={generateTimestamps}
                          onChange={(e) => setGenerateTimestamps(e.target.checked)}
                        />
                        <span>{t("upload.generateTimestamps")}</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {!isPremium && !focused && (
                <div className="upgrade-cta-bar" style={{marginTop: 8}}>
                  <div className="upgrade-cta-text">
                    {t("upload.premiumPitch")}
                  </div>
                  <UpgradeBillingActions
                    className="upgrade-billing-actions--bar"
                    signupFirst={signupFirst}
                    monthlyLabel={t("billing.transcribeFileNow")}
                    onUpgrade={onUpgrade}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer shows for recording mode, normal file uploads, or when podcast is resolved */}
        {(mode === 'recording' || (mode === 'upload' && !resolveData) || (mode === 'upload' && resolveData)) && (
          <div className="upload-popup-footer">
            <button className="secondary-button" onClick={handleCancel}>
              {t("common.cancel")}
            </button>

            {mode === 'upload' && !resolveData && (
              <button className="primary-button" onClick={handleSubmit} disabled={startDisabled}>
                {isFinalizingUpload && isUploading ? t("upload.finalizingUpload") : (isUploaded || hasJobId)
                  ? (isCountingDown
                    ? (isBatch ? t("upload.startAllCountdown", { count: countdown }) : t("upload.startOneCountdown", { count: countdown }))
                    : (isBatch ? t("upload.startAll") : t("upload.startTranscription")))
                  : (isFinalizingUpload ? t("upload.finalizingUpload") : t("upload.waitingUpload"))}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
